import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, LoadingSkeleton, PasswordInput, FilterBar, FilterSelect, FilterSearch } from './ui';
import { PencilIcon, KeyIcon, PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import AddAccountModal from './accounts/AddAccountModal';
import ChildrenModal from './accounts/ChildrenModal';

const ASSIGNABLE_ROLES = ['teacher', 'head_of_academics', 'coach', 'parent', 'student'];
const ROLE_BADGE = { administrator: 'primary', principal: 'primary', head_of_academics: 'primary', teacher: 'info', coach: 'warning', parent: 'default', student: 'default' };
const roleLabel = (role) => role.replace(/_/g, ' ');
const FILTER_ROLES = ['administrator', 'principal', 'head_of_academics', 'teacher', 'coach', 'parent', 'student'];

function StaffRoles({ token, user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({ username: '', email: '', password: '', is_active: true });
  const [accountError, setAccountError] = useState('');
  const [accountSaving, setAccountSaving] = useState(false);

  const [addingAccount, setAddingAccount] = useState(false);
  const [childrenOf, setChildrenOf] = useState(null);

  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/users', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setUsers(await response.json());
      } else {
        setError('Failed to load staff accounts');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openEditor = (row) => {
    setEditingUser(row);
    setSelectedRoles(row.additional_roles || []);
  };

  const toggleRole = (role) => {
    setSelectedRoles((prev) => prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/users/${editingUser.id}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ additional_roles: selectedRoles }),
      });
      if (response.ok) {
        const updated = await response.json();
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
        setEditingUser(null);
      } else {
        setError(await safeErrorMessage(response, 'Failed to update roles'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const openAccountEditor = (row) => {
    setEditingAccount(row);
    setAccountForm({ username: row.username, email: row.email, password: '', is_active: row.is_active });
    setAccountError('');
  };

  const handleAccountChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAccountSave = async () => {
    setAccountSaving(true);
    setAccountError('');
    try {
      const body = { username: accountForm.username, email: accountForm.email, is_active: accountForm.is_active };
      if (accountForm.password) body.password = accountForm.password;

      const response = await fetch(`/api/v1/users/${editingAccount.id}/account`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const updated = await response.json();
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
        setEditingAccount(null);
      } else {
        setAccountError(await safeErrorMessage(response, 'Failed to update account'));
      }
    } catch {
      setAccountError('Network error. Please check your connection and try again.');
    } finally {
      setAccountSaving(false);
    }
  };

  const term = search.trim().toLowerCase();
  // A role filter matches additional roles too, e.g. "coach" finds a teacher who also coaches
  const filteredUsers = users
    .filter((u) => !roleFilter || (u.all_roles || [u.role]).includes(roleFilter))
    .filter((u) => !statusFilter || (statusFilter === 'active') === Boolean(u.is_active))
    .filter((u) => !term || [u.full_name, u.username, u.email].some((value) => value?.toLowerCase().includes(term)));
  const filtersActive = Boolean(roleFilter || statusFilter || term);
  const clearFilters = () => { setRoleFilter(''); setStatusFilter(''); setSearch(''); };

  const columns = [
    { header: 'Name', render: (row) => <strong>{row.full_name}</strong> },
    { header: 'Username', accessor: 'username' },
    {
      header: 'Roles',
      render: (row) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
          <Badge variant={ROLE_BADGE[row.role] || 'default'}>{roleLabel(row.role)}</Badge>
          {row.additional_roles?.map((r) => (
            <Badge key={r} variant={ROLE_BADGE[r] || 'default'}>{roleLabel(r)}</Badge>
          ))}
        </div>
      ),
    },
    {
      header: 'Children',
      render: (row) => (row.all_roles?.includes('parent') ? (row.children_count ?? 0) : '—'),
    },
    { header: 'Status', render: (row) => <Badge variant={row.is_active ? 'success' : 'danger'}>{row.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      header: 'Actions',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" onClick={() => openEditor(row)}>
            <PencilIcon style={{ width: 14, height: 14 }} />
            Edit Roles
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openAccountEditor(row)}>
            <KeyIcon style={{ width: 14, height: 14 }} />
            Edit Account
          </Button>
          {row.all_roles?.includes('parent') && (
            <Button variant="ghost" size="sm" onClick={() => setChildrenOf(row)}>
              <UserGroupIcon style={{ width: 14, height: 14 }} />
              Children
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ flex: '1 1 320px' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>User Accounts</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            Create logins for teachers, parents, coaches and students. Edit Roles grants extra roles on top of someone's primary one — e.g. a teacher who is also Head of Academics or a coach. Children sets which students a parent can see.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setAddingAccount(true)}>
          <PlusIcon style={{ width: 16, height: 16 }} />
          Add account
        </Button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <FilterBar shown={filteredUsers.length} total={users.length} onClear={filtersActive ? clearFilters : undefined}>
        <FilterSearch value={search} onChange={setSearch} placeholder="Name, username or email" />
        <FilterSelect label="Role" value={roleFilter} onChange={setRoleFilter} options={FILTER_ROLES.map((role) => ({ value: role, label: roleLabel(role).charAt(0).toUpperCase() + roleLabel(role).slice(1) }))} allLabel="All roles" />
        <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} allLabel="Any status" />
      </FilterBar>

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={8} />
        ) : (
          <Table columns={columns} data={filteredUsers} emptyMessage={filtersActive ? 'No accounts match these filters.' : 'No accounts found.'} />
        )}
      </Card>

      <Modal isOpen={Boolean(editingUser)} onClose={() => setEditingUser(null)} title={editingUser ? `Edit Roles — ${editingUser.full_name}` : ''}>
        {editingUser && (
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
              Primary role: <strong>{roleLabel(editingUser.role)}</strong> (fixed — set at account creation). Choose any additional roles to grant.
            </p>
            <div className="form-group">
              <label>Additional Roles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', padding: '0.625rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                {ASSIGNABLE_ROLES.filter((r) => r !== editingUser.role).map((role) => (
                  <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.85rem', fontWeight: 400, cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} />
                    {roleLabel(role).charAt(0).toUpperCase() + roleLabel(role).slice(1)}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="secondary" onClick={() => setEditingUser(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={Boolean(editingAccount)} onClose={() => setEditingAccount(null)} title={editingAccount ? `Edit Account — ${editingAccount.full_name}` : ''}>
        {editingAccount && (
          <div>
            {accountError && <div className="alert alert-error">{accountError}</div>}
            <div className="form-group">
              <label>Username</label>
              <input type="text" name="username" value={accountForm.username} onChange={handleAccountChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={accountForm.email} onChange={handleAccountChange} required />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <PasswordInput name="password" value={accountForm.password} onChange={handleAccountChange} placeholder="Leave blank to keep current password" minLength={6} />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: 'fit-content', cursor: editingAccount.id === user?.id ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={accountForm.is_active}
                  onChange={handleAccountChange}
                  disabled={editingAccount.id === user?.id}
                  style={{ width: 'auto' }}
                />
                Active
              </label>
              {editingAccount.id === user?.id && (
                <small style={{ color: 'var(--color-text-muted)' }}>You cannot deactivate your own account.</small>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
              <Button variant="primary" onClick={handleAccountSave} disabled={accountSaving}>
                {accountSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="secondary" onClick={() => setEditingAccount(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>

      <AddAccountModal
        token={token}
        isOpen={addingAccount}
        onClose={() => setAddingAccount(false)}
        onCreated={(account) => setUsers((prev) => [...prev, account].sort((a, b) => a.full_name.localeCompare(b.full_name)))}
      />

      <ChildrenModal
        token={token}
        parent={childrenOf}
        onClose={() => setChildrenOf(null)}
        onSaved={(count) => {
          setUsers((prev) => prev.map((u) => (u.id === childrenOf.id ? { ...u, children_count: count } : u)));
          setChildrenOf(null);
        }}
      />
    </div>
  );
}

export default StaffRoles;
